import { type IdentifierRecord, type SectorId, type StagedDraftRecord } from '@myplayer/schema';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useState, type ReactElement } from 'react';
import { type AdminUserSession } from './auth-context.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
} from './components/ui/primitives.js';
import {
  getFirebaseClients,
  isFunctionsEmulatorOrLiveBackendConfigured,
  isLocalEmulatorMode,
} from './firebase.js';

export interface PendingPromotionState {
  readonly identifierKey: string;
  readonly scheduledMessage: string | null;
}

export interface DiscardIdentifierDraftCallablePayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly identifierKey: string;
  readonly reason?: string;
}

export interface DiscardIdentifierDraftCallableResult {
  readonly identifierKey: string;
  readonly discarded: boolean;
  readonly deletedDocument: boolean;
  readonly version: number;
  readonly discardedAt: string;
  readonly auditId: string;
}

export function applyLocalDraftRecordUpdate(
  prev: readonly IdentifierRecord[],
  identifierKey: string,
  nextDraft: StagedDraftRecord,
  uid: string,
  updatedSector?: SectorId,
  stagedOverride?: IdentifierRecord,
): IdentifierRecord[] {
  return prev.map((rec) => {
    if (rec.identifierKey !== identifierKey) return rec;
    if (stagedOverride !== undefined) return stagedOverride;
    const { draft: _omittedDraft, ...restWithoutDraft } = rec;
    return {
      ...restWithoutDraft,
      updatedBy: uid,
      updatedAt: nextDraft.submittedAt,
      version: (rec.version ?? 0) + 1,
      title: nextDraft.title,
      ...(nextDraft.brand !== undefined ? { brand: nextDraft.brand } : {}),
      defaultUrl: nextDraft.defaultUrl,
      links: nextDraft.links.filter((l) => l.removed !== true),
      rules: nextDraft.rules,
      ...(updatedSector !== undefined ? { sector: updatedSector } : {}),
      validation: { status: 'VALID', validatedAt: nextDraft.submittedAt, errors: [], warnings: [] },
    };
  });
}

export async function callDiscardIdentifierDraftFunction(
  payload: DiscardIdentifierDraftCallablePayload,
): Promise<DiscardIdentifierDraftCallableResult> {
  const invoke = async (): Promise<DiscardIdentifierDraftCallableResult> =>
    (
      await httpsCallable<
        DiscardIdentifierDraftCallablePayload,
        DiscardIdentifierDraftCallableResult
      >(
        getFirebaseClients().functions,
        'discardIdentifierDraft',
      )(payload)
    ).data;
  if (!import.meta.env.PROD && isLocalEmulatorMode()) {
    const fallbackResult: DiscardIdentifierDraftCallableResult = {
      identifierKey: payload.identifierKey,
      discarded: true,
      deletedDocument: false,
      version: 1,
      discardedAt: new Date().toISOString(),
      auditId: `aud_${payload.identifierKey}_discard_local`,
    };
    if (!isFunctionsEmulatorOrLiveBackendConfigured()) return fallbackResult;
    try {
      return await invoke();
    } catch {
      return fallbackResult;
    }
  }
  return invoke();
}

export async function discardProductDraftAction(params: {
  readonly session: AdminUserSession | null;
  readonly selectedRecord: IdentifierRecord | null;
  readonly canEdit: boolean;
  readonly isControlled: boolean;
  readonly onDiscardDraft?:
    | ((
        payload: DiscardIdentifierDraftCallablePayload,
      ) => Promise<DiscardIdentifierDraftCallableResult>)
    | undefined;
  readonly setDiscardingDraft: (v: boolean) => void;
  readonly setDiscardDraftError: (err: string | null) => void;
  readonly setLinkFormSuccess: (msg: string | null) => void;
  readonly onDiscarded: (result: {
    readonly identifierKey: string;
    readonly deletedDocument: boolean;
    readonly updatedRecord?: IdentifierRecord | undefined;
  }) => void;
}): Promise<void> {
  if (!params.session || !params.selectedRecord || !params.canEdit) return;
  const rec = params.selectedRecord;
  params.setDiscardDraftError(null);
  params.setDiscardingDraft(true);
  try {
    const hasLiveVersion =
      (rec.version !== undefined && rec.version > 0) ||
      (rec.defaultUrl !== undefined && rec.defaultUrl.trim().length > 0);
    const nowIso = new Date().toISOString();
    let deletedDocument = !hasLiveVersion;

    if (!params.isControlled || params.onDiscardDraft !== undefined) {
      const invokeDiscard = params.onDiscardDraft ?? callDiscardIdentifierDraftFunction;
      const res = await invokeDiscard({
        orgId: params.session.orgId,
        workspaceId: rec.workspaceId || params.session.activeWorkspaceId,
        identifierKey: rec.identifierKey,
      });
      deletedDocument = res.deletedDocument;
    }

    if (deletedDocument) {
      params.setLinkFormSuccess('Draft discarded');
      params.onDiscarded({ identifierKey: rec.identifierKey, deletedDocument: true });
      return;
    }

    const { draft: _removedDraft, ...withoutDraft } = rec;
    const restoredRecord: IdentifierRecord = {
      ...withoutDraft,
      updatedBy: params.session.uid,
      validation: { status: 'VALID', validatedAt: nowIso, errors: [], warnings: [] },
    };
    params.setLinkFormSuccess('Draft discarded');
    params.onDiscarded({
      identifierKey: rec.identifierKey,
      deletedDocument: false,
      updatedRecord: restoredRecord,
    });
  } catch (err: unknown) {
    params.setDiscardDraftError(
      err instanceof Error ? err.message : 'Could not discard draft right now. Please try again.',
    );
  } finally {
    params.setDiscardingDraft(false);
  }
}

export function usePendingPromotionTracker(params: {
  readonly records: readonly IdentifierRecord[];
  readonly selectedRecordKey?: string | undefined;
  readonly isControlled: boolean;
  readonly onDiscardDraft?:
    | ((
        payload: DiscardIdentifierDraftCallablePayload,
      ) => Promise<DiscardIdentifierDraftCallableResult>)
    | undefined;
  readonly session: AdminUserSession | null;
  readonly selectedRecord: IdentifierRecord | null;
  readonly canEdit: boolean;
  readonly setRecords: (updater: (prev: readonly IdentifierRecord[]) => IdentifierRecord[]) => void;
  readonly setSelectedKey: (key: string | null) => void;
  readonly pushProductPath: (path: string) => void;
  readonly setLinkFormSuccess: (msg: string | null) => void;
}) {
  const [pendingPromotion, setPendingPromotion] = useState<PendingPromotionState | null>(null);
  const [discardingDraft, setDiscardingDraft] = useState(false);
  const [discardDraftError, setDiscardDraftError] = useState<string | null>(null);

  useEffect(() => {
    if (!pendingPromotion) return;
    const target = params.records.find((r) => r.identifierKey === pendingPromotion.identifierKey);
    if (!target) return;
    if (target.validation?.status === 'INVALID') {
      setPendingPromotion(null);
      params.setLinkFormSuccess(null);
      return;
    }
    if (target.draft === undefined && target.validation?.status === 'VALID') {
      setPendingPromotion(null);
      params.setLinkFormSuccess(pendingPromotion.scheduledMessage ?? 'Saved — Live now');
    }
  }, [params.records, pendingPromotion, params.setLinkFormSuccess]);

  const handleDiscardDraft = async (): Promise<void> =>
    discardProductDraftAction({
      session: params.session,
      selectedRecord: params.selectedRecord,
      canEdit: params.canEdit,
      isControlled: params.isControlled,
      onDiscardDraft: params.onDiscardDraft,
      setDiscardingDraft,
      setDiscardDraftError,
      setLinkFormSuccess: params.setLinkFormSuccess,
      onDiscarded: (res) => {
        setPendingPromotion(null);
        if (res.deletedDocument) {
          params.setRecords((prev) => prev.filter((r) => r.identifierKey !== res.identifierKey));
          params.setSelectedKey(null);
          params.pushProductPath('/products');
          return;
        }
        if (res.updatedRecord !== undefined) {
          const updated = res.updatedRecord;
          params.setRecords((prev) =>
            prev.map((r) => (r.identifierKey === updated.identifierKey ? updated : r)),
          );
        }
      },
    });

  return {
    pendingPromotion,
    setPendingPromotion,
    isCheckingPromotion:
      pendingPromotion !== null && pendingPromotion.identifierKey === params.selectedRecordKey,
    discardingDraft,
    discardDraftError,
    handleDiscardDraft,
  };
}

export interface ProductReviewChangesCardProps {
  readonly selectedRecord: IdentifierRecord;
  readonly canEdit: boolean;
  readonly isCheckingPromotion?: boolean;
  readonly discardingDraft?: boolean;
  readonly discardDraftError?: string | null;
  readonly onDiscardDraft?: () => void;
}

export function ProductReviewChangesCard({
  selectedRecord,
  canEdit,
  isCheckingPromotion = false,
  discardingDraft = false,
  discardDraftError = null,
  onDiscardDraft,
}: ProductReviewChangesCardProps): ReactElement | null {
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const draft = selectedRecord.draft;
  if (!draft) return null;
  const hasBrandDiff = draft.brand !== undefined || selectedRecord.brand !== undefined;
  const stagedUrl =
    draft.defaultUrl ?? draft.links?.find((l) => l.defaultLink)?.href ?? 'Not set yet';
  const liveUrl =
    selectedRecord.defaultUrl && selectedRecord.defaultUrl.trim().length > 0
      ? selectedRecord.defaultUrl
      : 'Not published yet';
  const cols = [
    {
      colTestId: 'staged-values-column',
      heading: 'Staged (draft)',
      titleTestId: 'staged-draft-title',
      titleVal: draft.title,
      brandTestId: 'staged-draft-brand',
      brandVal: draft.brand ?? '—',
      urlTestId: 'staged-draft-url',
      urlVal: stagedUrl,
      cls: 'space-y-1.5',
    },
    {
      colTestId: 'live-values-column',
      heading: 'Live now',
      titleTestId: 'live-published-title',
      titleVal: selectedRecord.title ?? 'Not published yet',
      brandTestId: 'live-published-brand',
      brandVal: selectedRecord.brand ?? '—',
      urlTestId: 'live-published-url',
      urlVal: liveUrl,
      cls: 'space-y-1.5 border-t border-subtle pt-2 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-3',
    },
  ];
  return (
    <Card data-testid="review-changes-panel" className="border-status-warning-border">
      <CardHeader>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <CardTitle className="text-[14px]">Review changes</CardTitle>
          <Badge data-testid="review-changes-badge" variant="warning">
            {isCheckingPromotion ? 'Checking…' : 'Has unpublished changes'}
          </Badge>
          {selectedRecord.validation?.status === 'INVALID' && (
            <Badge data-testid="review-changes-rejected-badge" variant="destructive">
              Draft rejected
            </Badge>
          )}
        </div>
        {canEdit && onDiscardDraft !== undefined && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            data-testid="discard-draft-button"
            disabled={discardingDraft}
            onClick={() => setConfirmDiscardOpen(true)}
          >
            <span>{discardingDraft ? 'Discarding…' : 'Discard draft'}</span>
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[12px] text-fg-secondary">
          {selectedRecord.validation?.status === 'INVALID'
            ? 'Compare the rejected staged draft values against what is currently live for scanners.'
            : isCheckingPromotion
              ? 'Checking link safety and GS1 rules before publishing these staged changes…'
              : 'Compare the staged draft values waiting to publish against what is currently live for scanners.'}
        </p>
        {discardDraftError !== null && (
          <div
            role="alert"
            data-testid="discard-draft-error"
            className="rounded-control border border-status-error-border bg-status-error-bg px-3 py-2 text-[12px] text-status-error"
          >
            {discardDraftError}
          </div>
        )}
        <div
          data-testid="staged-vs-live-comparison"
          className="grid grid-cols-1 gap-3 rounded-control border border-subtle bg-surface-muted p-3 sm:grid-cols-2"
        >
          {cols.map((col) => (
            <div key={col.colTestId} className={col.cls} data-testid={col.colTestId}>
              <div className="text-[11px] font-semibold tracking-wider text-fg-secondary">
                {col.heading}
              </div>
              <div>
                <span className="block text-[11px] text-fg-secondary">Product name</span>
                <span data-testid={col.titleTestId} className="text-[12px] font-semibold text-fg">
                  {col.titleVal}
                </span>
              </div>
              {hasBrandDiff && (
                <div>
                  <span className="block text-[11px] text-fg-secondary">Brand</span>
                  <span data-testid={col.brandTestId} className="text-[12px] font-medium text-fg">
                    {col.brandVal}
                  </span>
                </div>
              )}
              <div>
                <span className="block text-[11px] text-fg-secondary">Where scans go</span>
                <span
                  data-testid={col.urlTestId}
                  className="block break-all font-mono text-[12px] font-semibold text-fg"
                >
                  {col.urlVal}
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      <ConfirmDialog
        open={confirmDiscardOpen}
        title="Discard unpublished changes?"
        description="Discard unpublished draft changes and keep the current live settings?"
        confirmLabel="Discard draft"
        cancelLabel="Cancel"
        variant="destructive"
        submitting={discardingDraft}
        onCancel={() => setConfirmDiscardOpen(false)}
        onConfirm={() => {
          setConfirmDiscardOpen(false);
          onDiscardDraft?.();
        }}
        data-testid="confirm-discard-draft-modal"
        confirmTestId="confirm-discard-draft-button"
        cancelTestId="cancel-discard-draft-button"
      />
    </Card>
  );
}
